import { useState } from 'react';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';

interface ParsedErrors {
  general?: string;
  fromArtistURI?: string;
  toArtistURI?: string;
  artistURI?: string;
}

const ERROR_MESSAGES = {
  FUZZY_NAMES_MISMATCH:
    'The artist names in our system do not match the metadata provided for this release. Please send updated metadata in the XML file to include the correct artist name.',
  ARTIST_NOT_FOUND: 'Artist is not found, please enter a valid URI/URL',
  DEFAULT: 'Invalid input, please check your entry',
  GENERIC: 'An error occurred while processing your request',
} as const;

// Helper function to parse structured error response
const parseEnrichardErrors = (responseData: any): ParsedErrors => {
  const errors: ParsedErrors = {};

  try {
    // Handle structured JSON format with validation_errors
    if (
      responseData.validation_errors &&
      Array.isArray(responseData.validation_errors)
    ) {
      for (const validationError of responseData.validation_errors) {
        const message =
          ERROR_MESSAGES[
            validationError.error_type as keyof typeof ERROR_MESSAGES
          ] || ERROR_MESSAGES.DEFAULT;

        if (validationError.error_type === 'FUZZY_NAMES_MISMATCH') {
          errors.general = message;
        } else {
          if (validationError.field === 'ARTIST_URI') {
            errors.toArtistURI = message;
          } else if (validationError.field === 'OTHER_ARTIST_URI') {
            errors.fromArtistURI = message;
          } else {
            errors.general = message;
          }
        }
      }
      return errors;
    }

    // Handle API error field (when service is down)
    if (responseData.error) {
      errors.general = ERROR_MESSAGES.GENERIC;
      return errors;
    }

    // Fallback for any other error format
    errors.general = ERROR_MESSAGES.GENERIC;
  } catch (parseError) {
    errors.general = ERROR_MESSAGES.GENERIC;
  }

  return errors;
};

export const useEnrichard = () => {
  const [status, setStatus] = useState<FETCH_STATUS>(FETCH_STATUS.IDLE);
  const [errors, setErrors] = useState<ParsedErrors>({});

  const setErrorState = (errorObj: ParsedErrors) => {
    setErrors(errorObj);
    setStatus(FETCH_STATUS.ERROR);
  };

  const postToEnrichard = async (body: string) => {
    setStatus(FETCH_STATUS.FETCHING);
    setErrors({});

    try {
      const res = await fetch('/catalog/api/enrichard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      if (!res.ok) {
        setErrorState({ general: ERROR_MESSAGES.GENERIC });
        return false;
      }

      const responseData = await res.json();

      if (
        responseData.success === false ||
        responseData.validation_errors ||
        responseData.error
      ) {
        const parsedErrors = parseEnrichardErrors(responseData);
        setErrorState(parsedErrors);
        return false;
      }

      setStatus(FETCH_STATUS.SUCCESS);
      return true;
    } catch (error) {
      setErrorState({ general: ERROR_MESSAGES.GENERIC });
      return false;
    }
  };

  const clearFieldError = (fieldName?: string) => {
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors.general;

      if (
        fieldName === 'destinationArtistURI' ||
        fieldName === 'myArtistURI' ||
        fieldName === 'currentArtistURI'
      ) {
        delete newErrors.toArtistURI;
      } else if (fieldName === 'incorrectArtistURI') {
        delete newErrors.fromArtistURI;
      } else if (fieldName?.includes('ArtistURI')) {
        delete newErrors.toArtistURI;
      }
      return newErrors;
    });
  };

  return {
    status,
    errors,
    actions: {
      postToEnrichard,
      clearFieldError,
    },
  };
};
